import type { ColumnType } from 'antd/es/table/interface'
import React, { useCallback } from 'react'

import { TableLabelFilterDropdown } from '../components/TableLabelFilterDropdown/TableLabelFilterDropdown'
import { columnFilterIcon } from './useColumnFilters'

export enum LabelFilterMode {
  ALL = 'ALL',
  ANY = 'ANY',
}

export function useLabelFilter<T>() {
  type LabelNode = {
    labelByLabelId?: { id?: string | null; name?: string | null } | null
  }
  type LabelsField = { nodes?: LabelNode[] | null } | null | undefined

  const getLabelFilter = useCallback(
    (dataIndex: keyof T): ColumnType<T> => ({
      ...columnFilterIcon,
      filterDropdown: ({
        setSelectedKeys,
        selectedKeys,
        confirm,
        clearFilters,
        close,
      }) => {
        const packed = (selectedKeys[0] as string) || ''
        return (
          <TableLabelFilterDropdown
            packed={packed}
            setSelectedKeys={setSelectedKeys}
            confirm={confirm}
            clearFilters={clearFilters}
            close={close}
          />
        )
      },
      onFilter: (value, record) => {
        if (!value) return true
        const parts = (value as string).split('|').filter(Boolean)

        if (!parts.length) return true
        const labelsContainer = record[dataIndex] as LabelsField
        const mode =
          parts[0] === LabelFilterMode.ANY || parts[0] === LabelFilterMode.ALL
            ? (parts[0] as LabelFilterMode)
            : LabelFilterMode.ALL

        const excludeIndex = parts.indexOf('EXCLUDE')
        const excludeIds =
          excludeIndex === -1 ? [] : parts.slice(excludeIndex + 1)

        const includeIds =
          excludeIndex === -1
            ? parts.slice(mode === parts[0] ? 1 : 0)
            : parts.slice(mode === parts[0] ? 1 : 0, excludeIndex)

        const recordIds: string[] = labelsContainer?.nodes
          ?.map(n => n?.labelByLabelId?.id || '')
          .filter(Boolean) as string[]

        let includePass = true
        if (includeIds.length) {
          includePass =
            mode === LabelFilterMode.ALL
              ? includeIds.every(id => recordIds.includes(id))
              : includeIds.some(id => recordIds.includes(id))
        }
        if (!includePass) return false

        if (excludeIds.length) {
          const hasExcluded = excludeIds.some(id => recordIds.includes(id))
          if (hasExcluded) return false
        }
        return true
      },
      filterDropdownProps: {},
    }),
    []
  )

  return { getLabelFilter }
}
